import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Volume2, KeyRound, Trash2, RefreshCw, Sparkles, Download, Upload, Globe } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getVoices, speak, isSupported } from "@/lib/tts";
import { t, LANGUAGES, ttsLangFor } from "@/lib/i18n";
import EmojiPicker from "@/components/EmojiPicker";
import PinPrompt from "@/components/PinPrompt";
import CloudBackup from "@/components/CloudBackup";
import BulkIllustrate from "@/components/BulkIllustrate";
import AccessStatusCard from "@/components/AccessStatusCard";
import NotesForChild from "@/components/NotesForChild";
import PendingProposalsInbox from "@/components/PendingProposalsInbox";
import AddOnPacksSection from "@/components/SettingsAddOnPacks";
import SettingsCreditsSection from "@/components/SettingsCreditsSection";
import SettingsParentalConsentSection from "@/components/SettingsParentalConsentSection";
import { ProgressReportsSection, DangerZoneSection } from "@/components/SettingsBottomSections";

const COLORS = ["#FFB627", "#FF6B47", "#6FAE6D", "#5AA9E6", "#F38BA8", "#C58A6B"];
const TILE_SIZES = [
  { id: "small", label: "Small (more tiles per screen)" },
  { id: "medium", label: "Medium (recommended)" },
  { id: "large", label: "Large (best for fine motor support)" },
];

export default function Settings() {
  const { profileId } = useParams();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [voices, setVoices] = useState([]);
  const [allVoices, setAllVoices] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyTemplate, setBusyTemplate] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pinPrompt, setPinPrompt] = useState({ open: false, error: "", mode: "set" });
  const [unlocked, setUnlocked] = useState(false);

  const lang = profile?.language || "en";

  const [addonPacks, setAddonPacks] = useState([]);
  const [addonBusy, setAddonBusy] = useState("");
  const [addonResult, setAddonResult] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/profiles/${profileId}`);
        setProfile(data);
        // PIN-gate Settings: if a PIN is set, lock the page until verified.
        if (data?.pin) {
          setUnlocked(false);
          setPinPrompt({ open: true, error: "", mode: "verify" });
        } else {
          setUnlocked(true);
        }
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
      const v = await getVoices();
      setAllVoices(v);
      try {
        const { data: ap } = await api.get(`/addon-packs`);
        setAddonPacks(ap.packs || []);
      } catch (err) {
        console.warn("Settings: addon packs fetch failed", err);
      }
    })();
  }, [profileId]);

  // When language changes, refresh the voice picker to that locale's voices
  useEffect(() => {
    if (!allVoices.length) return;
    const prefix = (profile?.language || "en");
    const filtered = allVoices.filter((x) => x.lang?.toLowerCase().startsWith(prefix));
    setVoices(filtered.length ? filtered : allVoices);
  }, [allVoices, profile?.language]);

  const update = (patch) => setProfile((p) => ({ ...p, ...patch }));

  const save = async () => {
    if (!profile) return;
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      const body = {
        name: profile.name,
        avatar: profile.avatar,
        color: profile.color,
        voice_rate: profile.voice_rate,
        voice_pitch: profile.voice_pitch,
        voice_name: profile.voice_name || null,
        tile_size: profile.tile_size,
        grid_density: parseInt(profile.grid_density || 4, 10),
        ai_suggestions: profile.ai_suggestions !== false,
        language: profile.language || "en",
        first_word_celebrated: !!profile.first_word_celebrated,
        dwell_ms: parseInt(profile.dwell_ms || 0, 10),
        scan_mode: !!profile.scan_mode,
        scan_interval_ms: parseInt(profile.scan_interval_ms || 1500, 10),
        onboarded: !!profile.onboarded,
        high_contrast: !!profile.high_contrast,
        reduced_motion: !!profile.reduced_motion,
        lockdown: !!profile.lockdown,
        modeling_mode: !!profile.modeling_mode,
        age_pack: profile.age_pack || "mixed",
        day_start: profile.day_start || "07:00",
        night_start: profile.night_start || "20:00",
        mode_override: profile.mode_override || "auto",
        category_layout: profile.category_layout || "auto",
        board_template: profile.board_template || null,
        night_tiles: Array.isArray(profile.night_tiles) ? profile.night_tiles : [],
      };
      const { data } = await api.put(`/profiles/${profileId}`, body);
      setProfile(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  const exportBoard = async () => {
    try {
      const { data } = await api.get(`/profiles/${profileId}/export`);
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sonspeak-${(data?.profile?.name || "board").toLowerCase().replace(/\s+/g, "-")}-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const importBoard = async (file) => {
    if (!file) return;
    if (!window.confirm(
      "Import this board? All current categories and tiles on this profile will be replaced."
    )) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      await api.post(`/profiles/${profileId}/import`, data);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Could not import that file.");
    }
  };

  const previewVoice = () => {
    if (!profile) return;
    const samples = {
      en: "Hi, I am here to help you talk.",
      es: "Hola, estoy aquí para ayudarte a hablar.",
      fr: "Salut, je suis là pour t'aider à parler.",
    };
    const lc = profile.language || "en";
    speak(samples[lc] || samples.en, {
      rate: profile.voice_rate,
      pitch: profile.voice_pitch,
      voiceName: profile.voice_name,
      lang: ttsLangFor(lc),
    });
  };

  const setPin = async (pin, errMsg) => {
    if (errMsg) {
      setPinPrompt((p) => ({ ...p, error: errMsg }));
      return;
    }
    // Page-load verify mode: just check the PIN matches and unlock.
    if (pinPrompt.mode === "verify") {
      if (pin === profile?.pin) {
        setUnlocked(true);
        setPinPrompt({ open: false, error: "", mode: "set" });
      } else {
        setPinPrompt((p) => ({ ...p, error: "Incorrect PIN" }));
      }
      return;
    }
    // Set / change mode
    try {
      await api.put(`/profiles/${profileId}/pin`, { pin });
      setProfile((p) => ({ ...p, pin }));
      setPinPrompt({ open: false, error: "", mode: "set" });
    } catch (e) {
      setPinPrompt((p) => ({ ...p, error: formatApiErrorDetail(e.response?.data?.detail) || e.message }));
    }
  };

  const removePin = async () => {
    if (!window.confirm("Remove caregiver PIN? Edit mode will no longer be locked.")) return;
    try {
      await api.delete(`/profiles/${profileId}/pin`);
      setProfile((p) => ({ ...p, pin: null }));
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const resetBoard = async () => {
    if (!window.confirm(
      "Reset this board to the default starter vocabulary?\n\nAll your custom tiles, recordings, and photos for this board will be permanently deleted. This can't be undone."
    )) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/profiles/${profileId}/reset-board`);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  const translateStarterTiles = async () => {
    if (!profile) return;
    const target = profile.language || "en";
    const langName = LANGUAGES.find((L) => L.code === target)?.nativeLabel || target;
    if (!window.confirm(
      `Replace this board's tiles with the default starter vocabulary in ${langName}?\n\nYour custom tiles, recordings, and photos on this board will be deleted. This is meant for boards still on the original starter set.`
    )) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/profiles/${profileId}/reset-board?lang=${encodeURIComponent(target)}`);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  const deleteAccount = async () => {
    const ok = window.confirm(
      "Delete your account and ALL boards, tiles, photos and recordings forever?\n\nThis cannot be undone."
    );
    if (!ok) return;
    const ok2 = window.prompt('Type "delete" to confirm') === "delete";
    if (!ok2) return;
    try {
      await api.delete("/auth/me");
      try { localStorage.removeItem("aac_access_token"); } catch { /* ignore */ }
      window.location.href = "/";
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const addAddonPack = async (packId) => {
    setAddonBusy(packId);
    setAddonResult("");
    try {
      const { data } = await api.post(`/profiles/${profileId}/add-pack?pack=${encodeURIComponent(packId)}`);
      setAddonResult(
        data.added
          ? `Added ${data.added} new tile${data.added > 1 ? "s" : ""} to ${data.category}.`
          : `Already on this board (${data.skipped} tiles skipped).`
      );
      setTimeout(() => setAddonResult(""), 3500);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setAddonBusy("");
  };

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="settings-loading">
        <div className="display text-2xl">{error || "Loading…"}</div>
      </div>
    );
  }

  // PIN-gate the whole Settings surface so kids can't undo lock from in-app.
  if (!unlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="settings-locked">
        <div className="brutal-card bg-white p-6 max-w-sm text-center">
          <div className="display text-2xl font-bold mb-1">{profile.name}</div>
          <div className="text-[color:var(--ink-soft)] mb-3">
            Settings are locked. Enter the caregiver PIN to continue.
          </div>
          <button
            type="button"
            className="pill-btn primary"
            onClick={() => setPinPrompt({ open: true, error: "", mode: "verify" })}
            data-testid="settings-locked-unlock"
          >
            <KeyRound size={16} /> Unlock
          </button>
        </div>
        <PinPrompt
          open={pinPrompt.open}
          mode={pinPrompt.mode || "verify"}
          error={pinPrompt.error}
          onClose={() => nav("/profiles")}
          onSubmit={setPin}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 sm:px-8 py-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/profiles" className="pill-btn" data-testid="settings-back">
            <ArrowLeft size={16} />
          </Link>
          <h1 className="display text-2xl sm:text-3xl font-extrabold">
            {profile.name}'s {t(lang, "settings.title")}
          </h1>
          <div className="flex-1" />
          <Link to={`/board/${profileId}`} className="pill-btn primary" data-testid="settings-open-board">
            Open board
          </Link>
        </div>

        {!isSupported() && (
          <div className="brutal-card bg-white p-3 mb-4 text-[color:var(--coral-deep)]">
            Your browser does not support speech synthesis. Tiles will still work but won't speak.
          </div>
        )}

        <AccessStatusCard />

        {/* Pending tile proposals — appears only when one or more proposals
            are awaiting parental (or clinical) sign-off. Sits at the very
            top of Settings so a parent who pops in finds it instantly. */}
        <PendingProposalsInbox />

        {/* Family Notes — parent-only one-way "you are loved" pulses to the
            child's board. Lives high in Settings (just under access status)
            so a parent who pops in mid-school-day finds it instantly. */}
        <div className="mb-4">
          <NotesForChild profileId={profileId} profileName={profile.name} />
        </div>

        {/* Profile section */}
        <section className="brutal-card bg-white p-5 mb-4">
          <h2 className="display text-xl font-bold mb-3">Profile</h2>
          <label className="block mb-3">
            <span className="display font-semibold mb-1 block">Name</span>
            <input
              className="brutal-input"
              value={profile.name}
              onChange={(e) => update({ name: e.target.value })}
              maxLength={60}
              data-testid="settings-name"
            />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-1">
            <div>
              <span className="display font-semibold mb-1 block">Avatar</span>
              <EmojiPicker value={profile.avatar} onChange={(e) => update({ avatar: e })} />
            </div>
            <div>
              <span className="display font-semibold mb-1 block">Color</span>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => update({ color: c })}
                    className={`w-10 h-10 rounded-full border-[3px] border-[color:var(--ink)] ${profile.color === c ? "ring-4 ring-[color:var(--gold)]" : ""}`}
                    style={{ background: c }}
                    aria-label={`Color ${c}`}
                    data-testid={`settings-color-${c}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Voice */}
        <section className="brutal-card bg-white p-5 mb-4">
          <h2 className="display text-xl font-bold mb-3">Voice</h2>
          <label className="block mb-3">
            <span className="display font-semibold mb-1 block">Voice</span>
            <select
              className="brutal-input"
              value={profile.voice_name || ""}
              onChange={(e) => update({ voice_name: e.target.value || null })}
              data-testid="settings-voice-name"
            >
              <option value="">Default (system best match)</option>
              {voices.map((v) => (
                <option key={v.name} value={v.name}>{v.name} — {v.lang}</option>
              ))}
            </select>
          </label>

          <label className="block mb-3">
            <span className="display font-semibold mb-1 block">
              Speed <span className="text-sm text-[color:var(--ink-soft)] font-normal">({profile.voice_rate.toFixed(2)}×)</span>
            </span>
            <input
              type="range" min="0.5" max="1.5" step="0.05"
              value={profile.voice_rate}
              onChange={(e) => update({ voice_rate: parseFloat(e.target.value) })}
              className="w-full"
              data-testid="settings-voice-rate"
            />
          </label>

          <label className="block mb-4">
            <span className="display font-semibold mb-1 block">
              Pitch <span className="text-sm text-[color:var(--ink-soft)] font-normal">({profile.voice_pitch.toFixed(2)})</span>
            </span>
            <input
              type="range" min="0.5" max="2" step="0.05"
              value={profile.voice_pitch}
              onChange={(e) => update({ voice_pitch: parseFloat(e.target.value) })}
              className="w-full"
              data-testid="settings-voice-pitch"
            />
          </label>

          <button type="button" className="pill-btn gold" onClick={previewVoice} data-testid="settings-voice-preview">
            <Volume2 size={16} /> {t(lang, "settings.voice.preview")}
          </button>
        </section>

        {/* Language & voice locale */}
        <section className="brutal-card bg-white p-5 mb-4">
          <h2 className="display text-xl font-bold mb-2 flex items-center gap-2">
            <Globe size={18} className="text-[color:var(--coral)]" />
            {t(lang, "settings.language")}
          </h2>
          <p className="text-[color:var(--ink-soft)] mb-3">
            {t(lang, "settings.language.help")}
          </p>
          <div className="flex flex-wrap gap-2">
            {LANGUAGES.map((L) => {
              const active = (profile.language || "en") === L.code;
              return (
                <button
                  key={L.code}
                  type="button"
                  onClick={() => update({ language: L.code, voice_name: null })}
                  className={`pill-btn ${active ? "gold" : ""}`}
                  aria-pressed={active}
                  data-testid={`settings-language-${L.code}`}
                >
                  <span style={{ fontSize: 18 }} aria-hidden>{L.flag}</span>
                  <span>{L.nativeLabel}</span>
                </button>
              );
            })}
          </div>
          {(profile.language || "en") !== "en" && (
            <div className="mt-3">
              <button
                type="button"
                className="pill-btn"
                onClick={translateStarterTiles}
                disabled={busy}
                data-testid="settings-translate-tiles"
              >
                <Globe size={16} /> Translate starter tiles to {LANGUAGES.find((L) => L.code === profile.language)?.nativeLabel}
              </button>
              <div className="text-xs text-[color:var(--ink-soft)] mt-1">
                Replaces the built-in tiles on this board with the localized starter set. Custom tiles will be removed.
              </div>
            </div>
          )}
        </section>

        {/* Accessibility — switch scanning + dwell time. The differentiator
            for kids with motor planning challenges. */}
        <section className="brutal-card bg-white p-5 mb-4">
          <h2 className="display text-xl font-bold mb-2">Accessibility</h2>
          <p className="text-[color:var(--ink-soft)] mb-4">
            Tools for kids whose hands don't always cooperate. Designed with motor-planning
            challenges in mind — turn on what helps, leave the rest off.
          </p>

          <div className="mb-4">
            <label className="block">
              <span className="display font-semibold mb-1 block">
                Touch &amp; hold (dwell) <span className="text-sm text-[color:var(--ink-soft)] font-normal">
                  ({Number(profile.dwell_ms || 0) === 0 ? "Off (instant tap)" : `${profile.dwell_ms} ms`})
                </span>
              </span>
              <input
                type="range" min="0" max="2000" step="100"
                value={profile.dwell_ms || 0}
                onChange={(e) => update({ dwell_ms: parseInt(e.target.value, 10) })}
                className="w-full"
                data-testid="settings-dwell-ms"
              />
              <div className="text-xs text-[color:var(--ink-soft)] mt-1">
                Holds down a tile for this long before it speaks. Helps kids who accidentally brush the screen.
              </div>
            </label>
          </div>

          <div className="mb-3">
            <label className="flex items-center gap-3 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={!!profile.scan_mode}
                onChange={(e) => update({ scan_mode: e.target.checked })}
                className="w-5 h-5 accent-[color:var(--coral)]"
                data-testid="settings-scan-mode"
              />
              <span className="display font-semibold">Switch scanning</span>
            </label>
            <div className="text-xs text-[color:var(--ink-soft)] mb-2">
              Auto-highlights tiles one at a time. Press <span className="font-mono">Space</span>,{" "}
              <span className="font-mono">Enter</span>, or anywhere on screen to select. For kids who use a switch
              or single-button input.
            </div>
            {profile.scan_mode && (
              <label className="block">
                <span className="display font-semibold mb-1 block">
                  Scan speed <span className="text-sm text-[color:var(--ink-soft)] font-normal">
                    ({(Number(profile.scan_interval_ms || 1500) / 1000).toFixed(1)}s per tile)
                  </span>
                </span>
                <input
                  type="range" min="500" max="3500" step="100"
                  value={profile.scan_interval_ms || 1500}
                  onChange={(e) => update({ scan_interval_ms: parseInt(e.target.value, 10) })}
                  className="w-full"
                  data-testid="settings-scan-interval"
                />
              </label>
            )}
          </div>

          <div className="border-t-[2px] border-[color:var(--paper-muted)] pt-4 mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!profile.high_contrast}
                onChange={(e) => update({ high_contrast: e.target.checked })}
                className="w-5 h-5 accent-[color:var(--coral)]"
                data-testid="settings-high-contrast"
              />
              <span className="display font-semibold">High-contrast tiles</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!profile.reduced_motion}
                onChange={(e) => update({ reduced_motion: e.target.checked })}
                className="w-5 h-5 accent-[color:var(--coral)]"
                data-testid="settings-reduced-motion"
              />
              <span className="display font-semibold">Reduce animations</span>
            </label>
          </div>
        </section>

        {/* Day/Night mode */}
        <section className="brutal-card bg-white p-5 mb-4" data-testid="settings-day-night">
          <h2 className="display text-xl font-bold mb-2">Day &amp; Night mode</h2>
          <p className="text-[color:var(--ink-soft)] mb-3">
            Night mode swaps to a softer, dimmer theme and narrows the board to a curated bedtime
            vocabulary so a 2am wake-up isn't a scrolling battle. Tiles get bigger, colors get muted,
            and your custom palette is respected — just dialed down.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            <label className="block">
              <span className="display font-semibold mb-1 block">Day starts</span>
              <input
                type="time"
                className="brutal-input"
                value={profile.day_start || "07:00"}
                onChange={(e) => update({ day_start: e.target.value })}
                data-testid="settings-day-start"
              />
            </label>
            <label className="block">
              <span className="display font-semibold mb-1 block">Night starts</span>
              <input
                type="time"
                className="brutal-input"
                value={profile.night_start || "20:00"}
                onChange={(e) => update({ night_start: e.target.value })}
                data-testid="settings-night-start"
              />
            </label>
            <div>
              <span className="display font-semibold mb-1 block">Mode</span>
              <div className="flex gap-1">
                {["auto", "day", "night"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => update({ mode_override: m })}
                    className={`pill-btn flex-1 justify-center ${(profile.mode_override || "auto") === m ? "gold" : ""}`}
                    aria-pressed={(profile.mode_override || "auto") === m}
                    data-testid={`settings-mode-${m}`}
                  >
                    {m === "auto" ? "Auto" : m === "day" ? "Day" : "Night"}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <span className="display font-semibold mb-1 block">Night vocabulary</span>
            <p className="text-xs text-[color:var(--ink-soft)] mb-2">
              Only tiles whose label is in this list show up at night. Match is case-insensitive against
              your real tile labels — keep them in sync if you rename. Tap × to remove. Press Enter to add.
            </p>
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="settings-night-tiles">
              {(profile.night_tiles || []).map((label, i) => (
                <span
                  key={`${label}-${i}`}
                  className="pill-btn gold"
                  data-testid={`night-tile-chip-${i}`}
                >
                  {label}
                  <button
                    type="button"
                    onClick={() =>
                      update({
                        night_tiles: (profile.night_tiles || []).filter((_, idx) => idx !== i),
                      })
                    }
                    className="ml-1 text-[color:var(--ink)] hover:text-[color:var(--coral-deep)]"
                    aria-label={`Remove ${label}`}
                    data-testid={`night-tile-remove-${i}`}
                  >
                    ×
                  </button>
                </span>
              ))}
              {(profile.night_tiles || []).length === 0 && (
                <span className="text-sm text-[color:var(--ink-soft)] italic">
                  No tiles set — night mode will fall back to the full board.
                </span>
              )}
            </div>
            <input
              type="text"
              className="brutal-input"
              placeholder="Type a tile label and press Enter (e.g. 'I'm scared')"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const v = e.target.value.trim();
                  if (!v) return;
                  const list = profile.night_tiles || [];
                  if (list.some((s) => s.toLowerCase() === v.toLowerCase())) {
                    e.target.value = "";
                    return;
                  }
                  update({ night_tiles: [...list, v] });
                  e.target.value = "";
                }
              }}
              data-testid="settings-night-tile-input"
            />
          </div>
        </section>

        {/* Kid-safety / Lockdown */}
        <section className="brutal-card bg-white p-5 mb-4">
          <h2 className="display text-xl font-bold mb-2">Kid-safety</h2>
          <p className="text-[color:var(--ink-soft)] mb-3">
            Lockdown hides the back arrow, settings, and First-Then nav from the board so a curious finger can't
            wander off to YouTube. To exit, hold the lock badge for 3 seconds.
          </p>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!profile.lockdown}
              onChange={(e) => update({ lockdown: e.target.checked })}
              className="w-5 h-5 accent-[color:var(--coral)]"
              data-testid="settings-lockdown"
            />
            <span className="display font-semibold">Lockdown mode on the board</span>
          </label>
          <div className="text-xs text-[color:var(--ink-soft)] mt-2">
            On iPad: pair this with <strong>Guided Access</strong> (Settings → Accessibility → Guided Access) to
            also lock the home button. Triple-click side button when home to start.
          </div>
        </section>

        {/* Tile size */}
        <section className="brutal-card bg-white p-5 mb-4">
          <h2 className="display text-xl font-bold mb-3">Tile size</h2>
          <div className="space-y-2">
            {TILE_SIZES.map((s) => (
              <label key={s.id} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="tile_size"
                  checked={profile.tile_size === s.id}
                  onChange={() => update({ tile_size: s.id })}
                  className="w-5 h-5 accent-[color:var(--coral)]"
                  data-testid={`settings-tile-size-${s.id}`}
                />
                <span>{s.label}</span>
              </label>
            ))}
          </div>
        </section>

        {/* Grid density — tiles per row */}
        <section className="brutal-card bg-white p-5 mb-4">
          <h2 className="display text-xl font-bold mb-1">Grid density</h2>
          <p className="text-[color:var(--ink-soft)] mb-3 text-sm">
            How many tiles fit on a row. Fewer = bigger tiles, gentler for early
            learners and motor support. More = denser board for advanced AAC users.
          </p>
          <div className="grid grid-cols-4 gap-2">
            {[
              { v: 2, label: "2", sub: "Early learner" },
              { v: 3, label: "3", sub: "Gentle" },
              { v: 4, label: "4", sub: "Balanced" },
              { v: 5, label: "5", sub: "Advanced" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => update({ grid_density: opt.v })}
                className={`brutal-card p-3 text-center transition ${
                  (profile.grid_density || 4) === opt.v
                    ? "bg-[color:var(--gold)] ring-4 ring-[color:var(--ink)]"
                    : "bg-white hover:bg-[color:var(--paper-muted)]"
                }`}
                data-testid={`settings-grid-density-${opt.v}`}
              >
                <div className="display text-2xl font-extrabold leading-none">{opt.label}</div>
                <div className="text-xs text-[color:var(--ink-soft)] mt-1">{opt.sub}</div>
              </button>
            ))}
          </div>
          <div className="mt-3 text-xs text-[color:var(--ink-soft)]">
            Tip: Night mode automatically drops by one density level so sleepy
            eyes get bigger targets.
          </div>
        </section>

        {/* Board size template — wipes & reseeds with focused vocab */}
        <section className="brutal-card bg-white p-5 mb-4" data-testid="settings-board-template">
          <h2 className="display text-xl font-bold mb-1">Board template</h2>
          <p className="text-sm text-[color:var(--ink-soft)] mb-3">
            Reset to a focused starter board. Compact for early learners,
            Standard for the essentials, Expanded for sentence building.
            <strong className="block mt-1 text-[color:var(--coral-deep)]">
              This will replace existing categories and tiles for {profile.name}.
            </strong>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[
              { v: "compact_6",   label: "Compact",   count: "6 tiles",   sub: "Early learner" },
              { v: "standard_12", label: "Standard",  count: "12 tiles",  sub: "Essentials" },
              { v: "expanded_18", label: "Expanded",  count: "18 tiles",  sub: "Sentence builder" },
              { v: "full_36",     label: "Full",      count: "36 tiles",  sub: "Day-to-day vocab" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                disabled={busyTemplate}
                onClick={async () => {
                  if (!window.confirm(`Replace ${profile.name}'s tiles with the ${opt.label} (${opt.count}) template? This cannot be undone.`)) return;
                  setBusyTemplate(true);
                  try {
                    await api.post(`/profiles/${profileId}/apply-template`, { template: opt.v });
                    window.location.reload();
                  } catch (e) {
                    alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                    setBusyTemplate(false);
                  }
                }}
                className={`brutal-card p-3 text-center transition ${
                  profile.board_template === opt.v
                    ? "bg-[color:var(--gold)] ring-4 ring-[color:var(--ink)]"
                    : "bg-white hover:bg-[color:var(--paper-muted)]"
                }`}
                data-testid={`settings-board-template-${opt.v}`}
              >
                <div className="display text-base font-extrabold leading-none">{opt.label}</div>
                <div className="text-xs text-[color:var(--ink-soft)] mt-1">{opt.count}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--coral-deep)] mt-1 font-bold">{opt.sub}</div>
              </button>
            ))}
          </div>
        </section>

        {/* Category layout — sidebar vs tabs */}
        <section className="brutal-card bg-white p-5 mb-4" data-testid="settings-category-layout">
          <h2 className="display text-xl font-bold mb-1">Category layout</h2>
          <p className="text-sm text-[color:var(--ink-soft)] mb-3">
            "Sidebar" puts categories on the left at every screen size — perfect
            for adults with large vocabularies. "Tabs" puts them in a horizontal
            scrolling strip on top. "Auto" stacks on phones, sidebar on tablets.
          </p>
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "auto",    label: "Auto",     sub: "Default" },
              { v: "sidebar", label: "Sidebar",  sub: "Adult / big vocab" },
              { v: "tabs",    label: "Tabs",     sub: "Top strip" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => update({ category_layout: opt.v })}
                className={`brutal-card p-3 text-center transition ${
                  (profile.category_layout || "auto") === opt.v
                    ? "bg-[color:var(--gold)] ring-4 ring-[color:var(--ink)]"
                    : "bg-white hover:bg-[color:var(--paper-muted)]"
                }`}
                data-testid={`settings-category-layout-${opt.v}`}
              >
                <div className="display text-base font-extrabold leading-none">{opt.label}</div>
                <div className="text-xs text-[color:var(--ink-soft)] mt-1">{opt.sub}</div>
              </button>
            ))}
          </div>
        </section>

        {/* Caregiver PIN */}
        <section className="brutal-card bg-white p-5 mb-4">
          <h2 className="display text-xl font-bold mb-2">Caregiver lock</h2>
          <p className="text-[color:var(--ink-soft)] mb-3">
            Set a 4-6 digit PIN so only you can enter Edit mode on the board.
          </p>
          {profile.pin ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="pill-btn"
                onClick={() => setPinPrompt({ open: true, error: "", mode: "set" })}
                data-testid="settings-pin-change"
              >
                <KeyRound size={16} /> {t(lang, "settings.pin.change")}
              </button>
              <button
                type="button"
                className="pill-btn danger"
                onClick={removePin}
                data-testid="settings-pin-remove"
              >
                <Trash2 size={16} /> {t(lang, "settings.pin.remove")}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="pill-btn primary"
              onClick={() => setPinPrompt({ open: true, error: "", mode: "set" })}
              data-testid="settings-pin-set"
            >
              <KeyRound size={16} /> {t(lang, "settings.pin.set")}
            </button>
          )}
        </section>

        {error && (
          <div className="brutal-card bg-white p-3 mb-4 text-[color:var(--coral-deep)]" data-testid="settings-error">
            {error}
          </div>
        )}

        {/* Reset board */}
        <section className="brutal-card bg-white p-5 mb-4">
          <h2 className="display text-xl font-bold mb-2 flex items-center gap-2">
            <Sparkles size={18} className="text-[color:var(--coral)]" />
            AI suggestions
          </h2>
          <p className="text-[color:var(--ink-soft)] mb-3">
            Show optional "suggested next word" pills above the sentence strip, drawn only from words already on the user's board. Off by default for telegraphic-style users.
          </p>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={profile.ai_suggestions !== false}
              onChange={(e) => update({ ai_suggestions: e.target.checked })}
              className="w-5 h-5 accent-[color:var(--coral)]"
              data-testid="settings-ai-suggestions"
            />
            <span className="display font-semibold">Enable AI suggestions</span>
          </label>
        </section>

        {/* Backup / restore */}
        <section className="brutal-card bg-white p-5 mb-4">
          <h2 className="display text-xl font-bold mb-2">Backup &amp; restore</h2>
          <p className="text-[color:var(--ink-soft)] mb-3">
            Download this entire board (every tile, photo, and recording) as a single JSON file you can email, save to iCloud, or share with another caregiver. Restore on any device.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="pill-btn"
              onClick={exportBoard}
              data-testid="settings-export"
            >
              <Download size={16} /> Export this board
            </button>
            <label className="pill-btn cursor-pointer" data-testid="settings-import-label">
              <Upload size={16} /> Import board
              <input
                type="file"
                accept="application/json"
                className="hidden"
                onChange={(e) => importBoard(e.target.files?.[0])}
                data-testid="settings-import-input"
              />
            </label>
          </div>
        </section>

        {/* Bulk illustrate the whole board */}
        <BulkIllustrate profileId={profileId} />

        {/* Cloud backup & restore */}
        <CloudBackup profileId={profileId} />

        {/* Reset board */}
        <section className="brutal-card bg-white p-5 mb-4">
          <h2 className="display text-xl font-bold mb-2">Reset board</h2>
          <p className="text-[color:var(--ink-soft)] mb-3">
            Wipe this board and start over with the latest starter vocabulary (Greetings, Feelings, I want, Body &amp; care, Activities, Food, People, Core words, My voice). Your custom tiles, photos and recordings on this board will be deleted.
          </p>
          <button
            type="button"
            className="pill-btn danger"
            onClick={resetBoard}
            disabled={busy}
            data-testid="settings-reset-board"
          >
            <RefreshCw size={16} /> Reset to default vocabulary
          </button>
        </section>

        {/* Pay-it-forward section removed 2026-07-05 per user directive.
            Was: donation pledge UI collecting email/name/amount + a stats
            counter showing "5 supporters · $25 pledged · 0 families
            covered." That data was populated from POST /donate/pledge —
            REAL user pledges, not seeded/fake — but the section was
            never authorized: it soliciteed money pre-Stripe, pre-EIN,
            pre-legal-review. The backend endpoints remain (data is
            preserved) but the UI surface is gone until proper billing
            and disclosure ship. */}

        {/* Coach AI credits — read-only transparency view */}
        <SettingsCreditsSection />

        {/* Public redeem-code UI struck per blueprint Design Handoff §12 —
            founder grants are admin-side only (GUTS Part J). */}

        {/* COPPA parental consent — read-only transparency view */}
        <SettingsParentalConsentSection />

        {/* Add-on vocabulary packs */}
        <AddOnPacksSection
          packs={addonPacks}
          busy={addonBusy}
          result={addonResult}
          onAdd={addAddonPack}
        />

        <ProgressReportsSection profile={profile} profileId={profileId} />

        <DangerZoneSection onDeleteAccount={deleteAccount} />

        <div className="flex justify-end gap-2">
          {saved && <span className="pill-btn gold pointer-events-none" data-testid="settings-saved">Saved ✓</span>}
          <button type="button" className="pill-btn primary" onClick={save} disabled={busy} data-testid="settings-save">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <PinPrompt
        open={pinPrompt.open}
        mode={pinPrompt.mode || "set"}
        error={pinPrompt.error}
        onClose={() => {
          // If page is still locked at load, bouncing back to /profiles is safer
          // than letting the user see Settings without unlocking.
          if (!unlocked) {
            nav("/profiles");
            return;
          }
          setPinPrompt({ open: false, error: "", mode: "set" });
        }}
        onSubmit={setPin}
      />
    </div>
  );
}
